/**
 * Design-based project estimate for the builder-facing lead detail view
 * (Round 23.2/23.3). The builder ALWAYS sees this range, regardless of the
 * tenant's estimate.showToCustomer flag (which only hides the range in the
 * customer PDF). Uses the tenant's own pricing (Round 23.3) when set, else
 * standard defaults.
 *
 * Consolidated onto @dockpro/core's `computeProjectEstimate` (R24.6) — the
 * same pricing math exportPDF's drawProjectEstimate uses, so a builder-seen
 * number can never silently drift from what a homeowner's PDF shows. The
 * portal's only job here is the lead-specific glue: hydrate a stored
 * (serialized) designState into take-off quantities, then delegate. Builder
 * pricing comes from the builder-authed /api/portal/tenant endpoint, NOT the
 * public /api/config (rates never leak to homeowners).
 */
import { computeDockModel, computeQuantities, computeProjectEstimate } from "@dockpro/core";
import { hydrateDesign } from "./exports/helpers";
import type { TenantPricing } from "./api";

/**
 * Compute a {low, high} estimate range from a lead's stored designState, using
 * the tenant's pricing when provided. Returns null when the design has no
 * usable geometry.
 */
export function estimateRangeFromDesign(
  designState: unknown,
  pricing?: TenantPricing | null,
  // round9/embed-config — the tenant's custom slip sizes, so a lead using
  // one is estimated WITH its slip (see exports/helpers.ts's hydrateDesign).
  customBoatBays?: unknown,
): { low: number; high: number } | null {
  const state = hydrateDesign(designState, { customBoatBays });
  if (!state) return null;
  const q = computeQuantities(computeDockModel(state));
  const { total, low, high } = computeProjectEstimate(q, pricing ?? undefined);
  if (!(total > 0)) return null;
  return { low, high };
}
